// app/context/modal-context.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  startTransition,
} from 'react';

// Basic product data that MainProductCard already has (for instant modal opening)
interface CachedProductData {
  id: string;
  name: string;
  strain?: string;
  brand?: string;
  category?: string;
  image?: string;
  primary_image?: string;
  price: number;
  salePrice?: number;
  loyaltyTierLabel?: string;
  discount?: string;
  thc?: string;
  classification?: string;
  slug: string;
  size?: string;
  product_description?: string;
  cbd?: string;
  onsale?: boolean;
  budflow_image_url?: string;
  all_images?: string[];
  budflow_flavors?: {
    group_id: string;
    group_name: string;
    products: string[];
  };
  budflow_mup?: {
    active: boolean;
    products: string[];
    rule_config: {
      quantity?: number;
      target_price?: number;
      discount_percent?: number;
      discount_amount?: number;
      minimum_spend?: number;
    };
    rule_id: string;
    rule_name: string;
    rule_type: string;
  };
  brand_logo_url?: string;
}

interface ModalContextType {
  isProductModalOpen: boolean;
  currentProductId: string | null;
  cachedProductData: CachedProductData | null;
  openProductModal: (productId: string, cachedData?: CachedProductData) => void;
  closeProductModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  // 🔒 SIMPLE RESTORE: Try to restore modal state from localStorage (accepts brief flash)
  // Uses unique key to avoid conflicts with other caching systems
  const STORAGE_KEY = 'catalyst_product_modal_preserve';
  const MAX_AGE_MS = 10000; // 10 seconds max age

  // Always start with modal closed to match server render (prevents hydration errors)
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [cachedProductData, setCachedProductData] = useState<CachedProductData | null>(null);
  const wasRestoredRef = useRef(false); // Track if we restored from localStorage
  const hasRestoredRef = useRef(false); // Prevent multiple restorations

  // 🔒 RESTORE AFTER HYDRATION: Restore modal state after client-side hydration completes
  // This prevents hydration mismatches (server renders closed, client restores open)
  useEffect(() => {
    // Only restore once after mount/hydration
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    // Guard: Only run in browser (not during SSR)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (!savedState) {
        return;
      }

      const parsed = JSON.parse(savedState);

      // Validate structure
      if (!parsed || typeof parsed !== 'object') {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const timestamp = parsed.timestamp || 0;
      const now = Date.now();
      const age = now - timestamp;

      // Check age and required fields
      if (
        age > MAX_AGE_MS ||
        age < 0 || // Invalid timestamp
        !parsed.isProductModalOpen ||
        !parsed.currentProductId ||
        typeof parsed.currentProductId !== 'string'
      ) {
        // Clean up invalid/stale data
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Valid state - restore it after a small delay to ensure hydration completes
      // Wrap in startTransition to prevent Suspense boundary hydration errors
      setTimeout(() => {
        startTransition(() => {
          setCurrentProductId(parsed.currentProductId);
          setIsProductModalOpen(true);
          wasRestoredRef.current = true;
        });
      }, 100); // Small delay to ensure hydration completes
    } catch (error) {
      // Safe error handling - clean up on any error
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
    }
  }, []); // Only run once on mount

  // 🔒 CLEANUP AFTER RESTORE: Clean up localStorage after modal is confirmed open
  useEffect(() => {
    if (wasRestoredRef.current && isProductModalOpen && currentProductId) {
      // Small delay to ensure modal is stable, then clean up
      const timer = setTimeout(() => {
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (error) {
          // Ignore cleanup errors
        }
        wasRestoredRef.current = false;
      }, 1500); // Give time for modal to stabilize
      return () => clearTimeout(timer);
    }
  }, [isProductModalOpen, currentProductId]);

  // 🔒 LISTEN FOR PRESERVE REQUESTS: Save modal state when login form requests it (before reload)
  useEffect(() => {
    // Guard: Only run in browser (not during SSR)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    const handlePreserveModalState = () => {
      // Only save if modal is actually open and we have a product ID
      if (!isProductModalOpen || !currentProductId) {
        return;
      }

      try {
        const modalState = {
          isProductModalOpen: true,
          currentProductId,
          timestamp: Date.now(),
        };

        // Validate before saving
        if (
          typeof modalState.currentProductId !== 'string' ||
          modalState.currentProductId.length === 0
        ) {
          return;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(modalState));
      } catch (error) {
        // Safe error handling - don't break the app if localStorage fails
        // Try to clean up on error
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (cleanupError) {
          // Ignore cleanup errors
        }
      }
    };

    window.addEventListener('preserveModalState', handlePreserveModalState);
    return () => window.removeEventListener('preserveModalState', handlePreserveModalState);
  }, [isProductModalOpen, currentProductId]);

  const openProductModal = (productId: string, cachedData?: CachedProductData) => {
    setCurrentProductId(productId);
    setCachedProductData(cachedData || null);
    setIsProductModalOpen(true);
  };

  const closeProductModal = () => {
    setIsProductModalOpen(false);
    // We keep the product ID and cached data until the modal is fully closed
    // to prevent visual glitches during the closing animation
    setTimeout(() => {
      setCurrentProductId(null);
      setCachedProductData(null);
    }, 300);
  };

  return (
    <ModalContext.Provider
      value={{
        isProductModalOpen,
        currentProductId,
        cachedProductData,
        openProductModal,
        closeProductModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
